import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import type { ColDef, GridApi, IServerSideDatasource } from 'ag-grid-community'

import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import type { AvailableFiltersDto } from '@b-stock/order-process-api-client'

import type { ManifestFilters } from './useManifestDatasource'
import { useManifestDatasource } from './useManifestDatasource'

export type VisibleRowRange = { firstRow: number; lastRow: number }

type ManifestContextValue = {
  availableFilters: AvailableFiltersDto[]
  columnDefs: ColDef[]
  datasource: IServerSideDatasource
  filters: ManifestFilters
  gridApi: GridApi | null
  isLoading: boolean
  limit: number
  listing: OrchestratedListingDetails
  offset: number
  search: string
  setColumnDefs: (value: ColDef[]) => void
  setFilters: (value: ManifestFilters) => void
  setGridApi: (value: GridApi | null) => void
  setSearch: (value: string) => void
  setVisibleRange: (value: VisibleRowRange | null) => void
  total: number
  visibleRange: VisibleRowRange | null
}

const ManifestContext = createContext<ManifestContextValue | null>(null)

export const ManifestProvider = ({
  children,
  limit,
  listing,
}: {
  children: React.ReactNode
  limit?: number
  listing: OrchestratedListingDetails
}) => {
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([])
  const [filters, setFilters] = useState<ManifestFilters>({})
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [search, setSearch] = useState('')
  const [visibleRange, setVisibleRangeState] = useState<VisibleRowRange | null>(
    null
  )
  // [BP-2376] Bail out on value-equal ranges: with the inline grid now able to
  // fire onBodyScroll continuously (see ManifestGrid's isInlineInfiniteScroll),
  // this avoids a new context value — and a re-render of every consumer — on
  // every scroll tick when the visible range hasn't actually changed.
  const setVisibleRange = useCallback((next: VisibleRowRange | null) => {
    setVisibleRangeState((prev) => {
      if (prev === next) return prev
      if (
        prev &&
        next &&
        prev.firstRow === next.firstRow &&
        prev.lastRow === next.lastRow
      ) {
        return prev
      }
      return next
    })
  }, [])
  const datasource = useManifestDatasource({ filters, limit, listing, search })

  const value = useMemo(
    () => ({
      ...datasource,
      columnDefs,
      filters,
      gridApi,
      listing,
      search,
      setColumnDefs,
      setFilters,
      setGridApi,
      setSearch,
      setVisibleRange,
      visibleRange,
    }),
    [
      columnDefs,
      datasource,
      filters,
      gridApi,
      listing,
      search,
      setVisibleRange,
      visibleRange,
    ]
  )
  return (
    <ManifestContext.Provider value={value}>
      {children}
    </ManifestContext.Provider>
  )
}

export const useManifestContext = (): ManifestContextValue => {
  const value = useContext(ManifestContext)
  if (!value) {
    throw new Error('useManifestContext must be used within ManifestProvider')
  }
  return value
}

/**
 * Convert a (0-based) visible row range to 1-based start/end for display.
 * Falls back to "1 to limit" before the first viewport event fires.
 */
export const getVisibleStartEnd = (
  visibleRange: VisibleRowRange | null,
  limit: number,
  total: number
): { start: number; end: number } => {
  if (visibleRange === null) {
    return { start: total === 0 ? 0 : 1, end: Math.min(limit, total) }
  }
  return {
    start: Math.min(visibleRange.firstRow + 1, total),
    end: Math.min(visibleRange.lastRow + 1, total),
  }
}
